/* eslint-disable react/prop-types */
import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";

import { styled } from "@mui/material/styles";

import { useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import PropTypes from "prop-types";

import axios from "axios";

import ReactQuill from "react-quill";

import { Loading, Block } from "notiflix/build/notiflix-loading-aio";

import Box from "@mui/material/Box";

import CircularProgress from "@mui/material/CircularProgress";

import MDBox from "components/MDBox";
import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";

import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import Edit from "@mui/icons-material/Edit";
import MDTypography from "components/MDTypography";
import Tooltip from "@mui/material/Tooltip";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import MDAvatar from "components/MDAvatar";
import ControlledTextInput from "components/FormInputs/ControlledTextInput";
import { Notify } from "notiflix/build/notiflix-notify-aio";

import Typography from "@mui/material/Typography";

import MDButton from "components/MDButton";
import { AddBox } from "@mui/icons-material";
import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";
import ProductImages from "./ProductImages";
import { replaceSingleQuoteToSQLFriendly } from "utils/helperFunctions";

import { addServerProductSchema } from "../schemas/_model";

import noPreview from "assets/images/no-preview.png";

import {
  useLazyGetAllServerProductsByCountryIdQuery,
  usePutServerProductMutation,
  useLazyGetServerProductByIdQuery,
} from "services/products";

import { resetStockId } from "store/entities/stockNotifications";

import { stripHTMLTags, imageSrcDecoder } from "utils/helperFunctions";

const modules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
    ["link", "image", "video"],
    ["clean"],
  ],
  clipboard: {
    // toggle to add extra line breaks when pasting HTML:
    matchVisual: false,
  },
};

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const EditServerProductDialog = ({ productName, productId }) => {
  const initialFormState = {
    price: 0,
    name: "",
    averageRating: 0,
    shortDescription: "",
    sku: "",
    options: [
      {
        category: "",
        name: "",
        price: 0,
        min_quantity: 0,
        max_quantity: 0,
      },
    ],
  };

  const dispatch = useDispatch();

  const [openEditDialog, setOpenEditDialog] = useState(false);

  const [openDigitalKeysDialog, setOpenDigitalKeysDialog] = useState(false);

  const [initialData, setInitialData] = useState({});

  const [editorDescriptionOnChange, setEditorDescriptionOnChange] = useState("");

  const {
    control,
    watch,
    handleSubmit,
    setValue,
    reset,
    resetField,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(addServerProductSchema),
    defaultValues: initialFormState,
  });

  const {
    fields: optionsFields,
    append: appendOptions,
    remove: removeOptions,
  } = useFieldArray({
    control,
    name: "options",
  });

  const [putProduct] = usePutServerProductMutation();

  const [triggerProductById] = useLazyGetServerProductByIdQuery();

  const handleClickOpen = async () => {
    setOpenEditDialog(true);
    Loading.dots({
      svgColor: "rgba(159, 3, 3, 0.8)",
    });
    triggerProductById(productId).then((res) => {
      setInitialData(res.data);
      reset({
        price: res.data.price,
        name: res.data.name,
        averageRating: res.data.averageRating,
        shortDescription: res.data.shortDescription,
        sku: res.data.sku,
        options: res.data.serverProductOptions,
      });
      setEditorDescriptionOnChange(res.data.description);
    });
    Loading.remove(500);
  };

  const handleClose = () => {
    setOpenEditDialog(false);
  };

  const onSubmit = async (data) => {
    if (editorDescriptionOnChange === undefined || editorDescriptionOnChange == "") {
      Notify.failure("You must add the product description!");
      return;
    }

    // setting up the loader

    Loading.dots({
      svgColor: "rgba(159, 3, 3, 0.8)",
    });

    let description = editorDescriptionOnChange ? editorDescriptionOnChange : "<p>&nbsp; </p>";

    data.description = replaceSingleQuoteToSQLFriendly(description);

    try {
      const putResponse = await putProduct({ id: productId, data });

      if (putResponse.data?.status == "success") {
        Notify.success("Product has been added successfully!");
        handleClose();
      } else {
        Notify.failure("Something went wrong. Please try again!");
      }
    } catch (e) {
    } finally {
      Loading.remove(500);
    }
  };

  return (
    <>
      <Tooltip title="Edit Product" placement="top">
        <IconButton aria-label="view" size="small" sx={{ ml: 1 }} onClick={handleClickOpen}>
          <Edit color="warning" fontSize="inherit" />
        </IconButton>
      </Tooltip>
      <BootstrapDialog
        // onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        maxWidth="xl"
        sx={{
          "& .MuiDialog-paper": {
            width: "1000px",
          },
        }}
        open={openEditDialog}
      >
        <DialogTitle sx={{ m: 0, p: 2, color: "#3f3f3f" }} id="customized-dialog-title">
          Edit Server Product - <strong>{productName}</strong>
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent className="productEditWrapper" dividers>
          <Box>
            <form>
              <MDBox
                display="flex"
                sx={{
                  padding: "20px",
                  border: "0.0625rem solid #f0f2f5",
                  borderRadius: "0.375rem",
                  position: "relative",
                  "& .deleteButton": {
                    position: "absolute",
                    top: "5px",
                    right: "5px",
                  },
                  "& .typeConvertButton": {
                    position: "absolute",
                    top: "5px",
                    right: "5px",
                  },
                  "& .productImagesBtn": {
                    position: "absolute",
                    bottom: "5px",
                    right: "5px",
                  },
                }}
              >
                <MDBox lineHeight={1}>
                  <Tooltip title="Click to view the product images" placement="top">
                    <MDAvatar
                      name={initialData.name}
                      src={
                        initialData.serverProductImages &&
                        initialData.serverProductImages.length > 0 &&
                        initialData.serverProductImages[0] &&
                        initialData.serverProductImages[0].url
                          ? imageSrcDecoder(initialData.serverProductImages[0].url)
                          : noPreview
                      }
                      // onLoad={() => setImageLoading(false)}
                      size="lg"
                      sx={{ mr: 3, cursor: "pointer" }}
                      onClick={() => setOpenDigitalKeysDialog(true)}
                    />
                  </Tooltip>
                  <ProductImages
                    productId={initialData.id}
                    openDigitalKeysDialog={openDigitalKeysDialog}
                    setOpenDigitalKeysDialog={setOpenDigitalKeysDialog}
                  />
                </MDBox>
                <MDBox lineHeight={1}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 400,
                      mb: 1,
                      color: "#3f3f3f",
                    }}
                  >
                    <strong>{productName}</strong>
                  </Typography>
                </MDBox>
              </MDBox>
              <br />
              <GridContainer>
                <GridItem xs={12} sm={12} md={4} lg={4} xl={4}>
                  <ControlledTextInput
                    name="name"
                    control={control}
                    labelText="Enter Name *"
                    formControlProps={{
                      fullWidth: true,
                    }}
                  />
                </GridItem>
                <GridItem xs={12} sm={12} md={4} lg={4} xl={4}>
                  <ControlledTextInput
                    name="sku"
                    control={control}
                    labelText="SKU"
                    formControlProps={{
                      fullWidth: true,
                    }}
                  />
                </GridItem>
                <GridItem xs={12} sm={12} md={4} lg={4} xl={4}>
                  <ControlledTextInput
                    name="price"
                    control={control}
                    labelText="Base Price *"
                    formControlProps={{
                      fullWidth: true,
                    }}
                    inputProps={{
                      type: "number",
                    }}
                  />
                </GridItem>

                <GridItem xs={12} sm={12}>
                  <Box
                    sx={{
                      border: "0.0625rem solid #f0f2f5",
                      padding: "10px",
                      mb: 3,
                      borderRadius: "0.375rem",
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" my={1}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 500,
                          color: "#3f3f3f",
                        }}
                      >
                        Options
                      </Typography>
                      <div>
                        <MDButton
                          color="info"
                          variant="gradient"
                          iconOnly
                          onClick={() => {
                            appendOptions({
                              category: "",
                              name: "",
                              price: 0,
                            });
                          }}
                        >
                          <AddBox sx={{}} />
                        </MDButton>
                      </div>
                    </Box>
                    <br />
                    {optionsFields.map((input, index) => {
                      return (
                        <GridContainer key={index}>
                          <GridItem xs={12} sm={11}>
                            <GridContainer key={index}>
                              <GridItem xs={12} sm={12} md={6} lg={6} xl={6}>
                                <ControlledTextInput
                                  id="category_key"
                                  name={`options.${index}.category`}
                                  control={control}
                                  labelText="Category *"
                                  formControlProps={{
                                    fullWidth: true,
                                  }}
                                />
                              </GridItem>

                              <GridItem xs={12} sm={12} md={6} lg={6} xl={6}>
                                <ControlledTextInput
                                  id="name_key"
                                  name={`options.${index}.name`}
                                  control={control}
                                  labelText="Name *"
                                  formControlProps={{
                                    fullWidth: true,
                                  }}
                                />
                              </GridItem>
                            </GridContainer>
                            <GridContainer key={index}>
                              <GridItem xs={12} sm={12} md={4} lg={4} xl={4}>
                                <ControlledTextInput
                                  id={`price_key_${index}`}
                                  name={`options.${index}.price`}
                                  control={control}
                                  labelText="Price *"
                                  formControlProps={{
                                    fullWidth: true,
                                  }}
                                  inputProps={{
                                    type: "number",
                                  }}
                                />
                              </GridItem>
                              <GridItem xs={12} sm={12} md={4} lg={4} xl={4}>
                                <ControlledTextInput
                                  id={`min_quantity_key_${index}`}
                                  name={`options.${index}.min_quantity`}
                                  control={control}
                                  labelText="Min Quantity *"
                                  formControlProps={{
                                    fullWidth: true,
                                  }}
                                  inputProps={{
                                    type: "number",
                                  }}
                                />
                              </GridItem>
                              <GridItem xs={12} sm={12} md={4} lg={4} xl={4}>
                                <ControlledTextInput
                                  id={`max_quantity_key_${index}`}
                                  name={`options.${index}.max_quantity`}
                                  control={control}
                                  labelText="Max Quantity *"
                                  formControlProps={{
                                    fullWidth: true,
                                  }}
                                  inputProps={{
                                    type: "number",
                                  }}
                                />
                              </GridItem>
                            </GridContainer>
                          </GridItem>
                          <GridItem xs={12} sm={1}>
                            <MDButton
                              color="warning"
                              variant="gradient"
                              iconOnly
                              onClick={() => removeOptions(index)}
                            >
                              <IndeterminateCheckBoxIcon />
                            </MDButton>
                          </GridItem>
                        </GridContainer>
                      );
                    })}
                  </Box>
                </GridItem>
                <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
                  <ControlledTextInput
                    multiline={true}
                    name="shortDescription"
                    control={control}
                    labelText="Short description *"
                    formControlProps={{
                      fullWidth: true,
                    }}
                  />
                </GridItem>
                <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
                  <MDBox
                    sx={{
                      border: "1px solid #d2d6da",
                      borderRadius: "0.375rem",
                      mb: 3,
                      pt: 2,
                      position: "relative",
                      // "& .tox-tinymce": { border: "none", position: "relative" },
                      "& .ql-editor": { minHeight: "300px" },
                    }}
                  >
                    <MDTypography
                      component="p"
                      color="info"
                      sx={{
                        lineHeight: "1.5",
                        fontSize: "0.8rem",
                        fontWeight: 500,
                        transform: "translate(7px, -50%)",
                        backgroundColor: "#fff",
                        color: "#7b809a",
                        padding: "0 5px",
                        position: "absolute",
                        left: 0,
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      Description
                    </MDTypography>
                    {/* <TinyMCEEditor initialContent={""} realTimeContent={setEditorDescriptionOnChange} /> */}
                    <ReactQuill
                      theme="snow"
                      value={editorDescriptionOnChange}
                      onChange={setEditorDescriptionOnChange}
                      modules={modules}
                    />
                  </MDBox>
                </GridItem>

                <GridItem xs={12} sm={12} md={4} lg={4} xl={4}>
                  <ControlledTextInput
                    name="averageRating"
                    control={control}
                    labelText="Average rating *"
                    formControlProps={{
                      fullWidth: true,
                    }}
                    inputProps={{
                      type: "number",
                    }}
                  />
                </GridItem>
              </GridContainer>
            </form>
            <DialogActions>
              * Required fields
              <Box sx={{ marginLeft: "auto" }}>
                <MDButton
                  color="primary"
                  variant="gradient"
                  id="submit"
                  onClick={handleSubmit(onSubmit)}
                >
                  Update Product
                </MDButton>
                <MDButton
                  color="secondary"
                  variant="gradient"
                  onClick={() => handleClose()}
                  sx={{ ml: 1 }}
                >
                  Cancel
                </MDButton>
              </Box>
            </DialogActions>
          </Box>
        </DialogContent>
      </BootstrapDialog>
    </>
  );
};

EditServerProductDialog.propTypes = {
  // children: PropTypes.node.isRequired, // You can also use PropTypes.element if you want to enforce a single element.
  // productData: PropTypes.object.isRequired,
};

export default EditServerProductDialog;
